import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import { Banner, Loading, formatDateTime, errorMessage } from '../components/ui';
import './Settings.css';

const CGS = 'cgs';

const TABS = [
  { key: 'automation', label: 'Automation', superAdminOnly: true },
  { key: 'certificate', label: 'Certificate of Good Standing', superAdminOnly: true },
  { key: 'notifications', label: 'Notifications' },
  { key: 'sheet', label: 'Google Sheet' },
  { key: 'profile', label: 'My profile' },
  { key: 'health', label: 'System health' },
];

/**
 * Settings are grouped into cards that sit side by side, so related controls
 * read together instead of scrolling past each other in one long column.
 */
const GROUPS = {
  automation: [
    {
      title: 'Google Form intake',
      hint: 'How often the app looks for new submissions, and whether arrivals are announced.',
      keys: ['sheet_poll_enabled', 'sheet_poll_interval_minutes', 'notify_managers_on_new_application'],
    },
    {
      title: 'Action list',
      hint: 'The recurring list of new applications, renewals due and anything else waiting. '
        + 'Who receives it is set under Notifications.',
      keys: ['reminder_enabled', 'reminder_frequency', 'reminder_day_of_week',
        'reminder_send_hour', 'reminder_only_if_pending'],
    },
    {
      title: 'Certificate expiry warning',
      hint: 'The renewal trigger — warns managers before an issued certificate lapses.',
      keys: ['expiry_reminder_enabled', 'expiry_reminder_days_before', 'expiry_reminder_send_hour'],
    },
  ],
  certificate: [
    {
      title: 'Validity',
      hint: 'How long a certificate stays valid once issued.',
      keys: ['certificate_validity_months'],
    },
    {
      title: 'Wording',
      hint: 'Text built into every generated certificate and letter.',
      keys: ['regulation_preamble', 'commodity_field_format'],
    },
    {
      title: 'Email',
      hint: 'Who is copied on client emails.',
      keys: ['email_cc'],
    },
  ],
};

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const CHOICES = {
  commodity_field_format: [
    { value: 'short', label: 'Short — strip trailing "Certificate"' },
    { value: 'full', label: 'Full — exactly as submitted' },
  ],
  reminder_frequency: [
    { value: 'weekly', label: 'Weekly — on the day below' },
    { value: 'daily', label: 'Daily' },
  ],
  reminder_day_of_week: DAYS.map((d, i) => ({ value: String(i), label: d })),
};

/** Compact labels — the long-form description becomes the row hint. */
const LABELS = {
  sheet_poll_enabled: 'Check the sheet automatically',
  sheet_poll_interval_minutes: 'Check every (minutes)',
  reminder_enabled: 'Send the action list',
  reminder_frequency: 'How often',
  reminder_day_of_week: 'On',
  reminder_send_hour: 'At hour (0–23)',
  reminder_only_if_pending: 'Only when work is waiting',
  expiry_reminder_enabled: 'Send expiry warnings',
  expiry_reminder_days_before: 'Days before expiry',
  expiry_reminder_send_hour: 'At hour (0–23)',
  certificate_validity_months: 'Valid for (months)',
  regulation_preamble: 'Certificate preamble',
  commodity_field_format: 'Commodity wording in letters',
  email_cc: 'Always CC these users',
  notify_managers_on_new_application: 'Alert on new application',
};

function Settings() {
  let currentUser = {};
  try { currentUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch { currentUser = {}; }
  const isSuperAdmin = currentUser.role === 'Super Admin';

  const [tab, setTab] = useState(isSuperAdmin ? 'automation' : 'notifications');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState('');

  const [values, setValues] = useState({});
  const [definitions, setDefinitions] = useState({});
  const [profile, setProfile] = useState(null);

  const [modules, setModules] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [mySubs, setMySubs] = useState(null);
  const [notifTypes, setNotifTypes] = useState([]);

  const [health, setHealth] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [mapping, setMapping] = useState({});

  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [sigVersion, setSigVersion] = useState(0);

  // Registered users, for the "always CC" picker on the certificate tab.
  const [users, setUsers] = useState(null);

  const [recipients, setRecipients] = useState(null);
  const [newRecipient, setNewRecipient] = useState({
    email: '', name: '',
    NotifyNewApplication: false, NotifyPendingWork: false, NotifyExpiry: true,
  });

  const loadCore = useCallback(async () => {
    setLoading(true);
    try {
      const [s, me, mods] = await Promise.all([
        axios.get(`${API_BASE}/api/settings`),
        axios.get(`${API_BASE}/api/auth/me`),
        axios.get(`${API_BASE}/api/modules`),
      ]);
      setValues(s.data.values);
      setDefinitions(s.data.definitions);
      setProfile(me.data);
      setModules(mods.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCore(); }, [loadCore]);

  useEffect(() => {
    if (tab === 'certificate' && isSuperAdmin && !users) {
      axios.get(`${API_BASE}/api/users`)
        .then((r) => setUsers(r.data.filter((u) => u.isActive)))
        .catch((e) => setError(errorMessage(e, 'Could not load the user list.')));
    }
    if (tab === 'notifications') {
      axios.get(`${API_BASE}/api/modules/me/subscriptions`)
        .then((r) => { setMySubs(r.data.modules); setNotifTypes(r.data.types); })
        .catch((e) => setError(errorMessage(e)));
      if (isSuperAdmin) {
        axios.get(`${API_BASE}/api/modules/${CGS}/notifications`)
          .then((r) => { setMatrix(r.data.users); setNotifTypes(r.data.types); })
          .catch((e) => setError(errorMessage(e)));
        axios.get(`${API_BASE}/api/modules/${CGS}/recipients`)
          .then((r) => setRecipients(r.data.recipients))
          .catch((e) => setError(errorMessage(e)));
      }
    }
    if (tab === 'sheet' && !sheet) {
      axios.get(`${API_BASE}/api/settings/sheet-mapping`)
        .then((r) => { setSheet(r.data); setMapping(r.data.mapping || r.data.suggested || {}); })
        .catch((e) => setError(errorMessage(e)));
    }
    if (tab === 'health' && !health) {
      Promise.all([
        axios.get(`${API_BASE}/api/settings/health`).catch((e) => e.response || { data: {} }),
        axios.get(`${API_BASE}/api/scheduler`).catch(() => ({ data: null })),
      ]).then(([h, s]) => { setHealth(h.data); setScheduler(s.data); });
    }
  }, [tab, sheet, health, users, isSuperAdmin]);

  const cgsModule = modules.find((m) => m.Key === CGS);

  // ---------------------------------------------------------------- actions

  const saveSettings = async (keys) => {
    setBusy('save'); setError(''); setSuccess('');
    try {
      const payload = {};
      keys.forEach((k) => { payload[k] = values[k]; });
      const res = await axios.put(`${API_BASE}/api/settings`, payload);
      setValues(res.data.values);
      setSuccess('Settings saved.');
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const uploadModuleSignature = async (file) => {
    if (!file) return;
    setBusy('modsig'); setError('');
    try {
      const data = new FormData();
      data.append('signature', file);
      const r = await axios.post(`${API_BASE}/api/modules/${CGS}/signature`, data,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(r.data.message);
      setSigVersion((v) => v + 1);
      await loadCore();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const removeModuleSignature = async () => {
    setBusy('modsig');
    try {
      await axios.delete(`${API_BASE}/api/modules/${CGS}/signature`);
      setSuccess('Signature removed.');
      setSigVersion((v) => v + 1);
      await loadCore();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const toggleMySub = async (moduleKey, typeKey, checked) => {
    const next = mySubs.map((m) => (m.ModuleKey === moduleKey ? { ...m, [typeKey]: checked } : m));
    setMySubs(next);
    const row = next.find((m) => m.ModuleKey === moduleKey);
    try {
      await axios.put(`${API_BASE}/api/modules/me/subscriptions/${moduleKey}`, {
        NotifyNewApplication: !!row.NotifyNewApplication,
        NotifyPendingWork: !!row.NotifyPendingWork,
        NotifyExpiry: !!row.NotifyExpiry,
      });
      setSuccess('Your notification settings are saved.');
    } catch (err) { setError(errorMessage(err)); }
  };

  const toggleMatrix = async (userId, typeKey, checked) => {
    const next = matrix.map((u) => (u.UserId === userId ? { ...u, [typeKey]: checked } : u));
    setMatrix(next);
    const row = next.find((u) => u.UserId === userId);
    try {
      await axios.put(`${API_BASE}/api/modules/${CGS}/notifications/${userId}`, {
        NotifyNewApplication: !!row.NotifyNewApplication,
        NotifyPendingWork: !!row.NotifyPendingWork,
        NotifyExpiry: !!row.NotifyExpiry,
      });
      setSuccess('Notification settings saved.');
    } catch (err) { setError(errorMessage(err)); }
  };

  const reloadRecipients = async () => {
    const r = await axios.get(`${API_BASE}/api/modules/${CGS}/recipients`);
    setRecipients(r.data.recipients);
  };

  const addRecipient = async () => {
    setBusy('addrecipient'); setError(''); setSuccess('');
    try {
      const r = await axios.post(`${API_BASE}/api/modules/${CGS}/recipients`, newRecipient);
      setSuccess(r.data.message);
      setNewRecipient({
        email: '', name: '',
        NotifyNewApplication: false, NotifyPendingWork: false, NotifyExpiry: true,
      });
      await reloadRecipients();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const toggleRecipient = async (id, typeKey, checked) => {
    const next = recipients.map((r) => (r.Id === id ? { ...r, [typeKey]: checked } : r));
    setRecipients(next);
    const row = next.find((r) => r.Id === id);
    try {
      await axios.put(`${API_BASE}/api/modules/${CGS}/recipients/${id}`, {
        name: row.Name,
        NotifyNewApplication: !!row.NotifyNewApplication,
        NotifyPendingWork: !!row.NotifyPendingWork,
        NotifyExpiry: !!row.NotifyExpiry,
        IsActive: !!row.IsActive,
      });
      setSuccess('Recipient updated.');
    } catch (err) { setError(errorMessage(err)); await reloadRecipients(); }
  };

  const removeRecipient = async (row) => {
    if (!window.confirm(`Stop sending notifications to ${row.Email}?`)) return;
    try {
      const r = await axios.delete(`${API_BASE}/api/modules/${CGS}/recipients/${row.Id}`);
      setSuccess(r.data.message);
      await reloadRecipients();
    } catch (err) { setError(errorMessage(err)); }
  };

  const runExpirySweep = async () => {
    setBusy('sweep'); setError('');
    try {
      const r = await axios.post(`${API_BASE}/api/scheduler/run-expiry-sweep`);
      setSuccess(r.data.message);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const changePassword = async () => {
    if (passwords.newPassword !== passwords.confirm) {
      setError('The new password and confirmation do not match.'); return;
    }
    setBusy('password'); setError('');
    try {
      await axios.post(`${API_BASE}/api/auth/change-password`, {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setSuccess('Password changed.');
      setPasswords({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const sendTestEmail = async () => {
    setBusy('testmail'); setError('');
    try {
      const r = await axios.post(`${API_BASE}/api/settings/test-email`);
      setSuccess(r.data.message);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const sendActionListNow = async () => {
    setBusy('digest'); setError('');
    try {
      const r = await axios.post(`${API_BASE}/api/scheduler/run-action-digest`);
      setSuccess(r.data.message);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const saveMapping = async () => {
    setBusy('mapping'); setError('');
    try {
      await axios.put(`${API_BASE}/api/settings/sheet-mapping`, { mapping });
      setSuccess('Column mapping saved.');
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const autoDetect = async () => {
    setBusy('detect'); setError('');
    try {
      const r = await axios.post(`${API_BASE}/api/settings/sheet-mapping/auto-detect`);
      setMapping(r.data.suggested);
      setSuccess('Suggested from the sheet headers — review, then save.');
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  // ---------------------------------------------------------------- render

  if (loading) {
    return <div className="page-container"><div className="page-card"><Loading /></div></div>;
  }

  /** One setting as a compact label-left / control-right row. */
  const renderRow = (key) => {
    const def = definitions[key];
    if (!def) return null;
    const value = values[key];
    const label = LABELS[key] || key;

    if (def.type === 'boolean') {
      return (
        <div className="set-row" key={key}>
          <span className="set-row-label">{label}</span>
          <label className="set-switch">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => setValues({ ...values, [key]: e.target.checked })}
            />
            <span className="set-slider" />
          </label>
        </div>
      );
    }

    if (CHOICES[key]) {
      // Only meaningful on a weekly cadence — hide it rather than leave a
      // control that silently does nothing.
      if (key === 'reminder_day_of_week' && values.reminder_frequency === 'daily') return null;

      return (
        <div className="set-row set-row-wide" key={key}>
          <span className="set-row-label">{label}</span>
          <select
            className="set-control"
            value={String(value ?? '')}
            onChange={(e) => setValues({ ...values, [key]: e.target.value })}
          >
            {CHOICES[key].map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      );
    }

    // Picked from registered users rather than typed, so a standing CC can
    // never be a stale or mistyped address. Stored comma-separated.
    if (key === 'email_cc') {
      const chosen = String(value || '').split(/[,;]/).map((e) => e.trim()).filter(Boolean);
      const isOn = (email) => chosen.some((c) => c.toLowerCase() === email.toLowerCase());
      const toggle = (email) => {
        const next = isOn(email)
          ? chosen.filter((c) => c.toLowerCase() !== email.toLowerCase())
          : [...chosen, email];
        setValues({ ...values, email_cc: next.join(', ') });
      };

      // An address that is no longer an active user still has to be visible,
      // or it would keep being copied with nothing on screen to explain why.
      const known = (users || []).map((u) => u.email.toLowerCase());
      const orphans = chosen.filter((c) => !known.includes(c.toLowerCase()));

      return (
        <div className="set-row set-row-stack" key={key}>
          <span className="set-row-label">{label}</span>
          {!users ? <Loading /> : (
            <div className="set-picker">
              {users.length === 0 && <p className="set-card-hint">No active users.</p>}
              {users.map((u) => (
                <label className="set-picker-item" key={u.id}>
                  <input type="checkbox" checked={isOn(u.email)} onChange={() => toggle(u.email)} />
                  <span>
                    <strong>{u.firstName} {u.lastName}</strong>
                    <span className="set-sub">{u.email}</span>
                  </span>
                </label>
              ))}
              {orphans.map((email) => (
                <label className="set-picker-item set-picker-orphan" key={email}>
                  <input type="checkbox" checked onChange={() => toggle(email)} />
                  <span>
                    <strong>{email}</strong>
                    <span className="set-sub">no longer an active user — untick to remove</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          <span className="field-hint">
            {chosen.length === 0
              ? 'Nobody is copied as standing CC. The inspector allocated to an application is always copied.'
              : `${chosen.length} copied on every client email, on top of the allocated inspector.`}
          </span>
        </div>
      );
    }

    if (key === 'regulation_preamble') {
      return (
        <div className="set-row set-row-stack" key={key}>
          <span className="set-row-label">{label}</span>
          <textarea
            className="set-control"
            rows={3}
            value={value ?? ''}
            onChange={(e) => setValues({ ...values, [key]: e.target.value })}
          />
        </div>
      );
    }

    const isNumber = def.type === 'number';
    return (
      <div className={`set-row${isNumber ? '' : ' set-row-wide'}`} key={key}>
        <span className="set-row-label">{label}</span>
        <input
          className={`set-control${isNumber ? ' set-control-num' : ''}`}
          type={isNumber ? 'number' : 'text'}
          value={value ?? ''}
          onChange={(e) => setValues({ ...values, [key]: e.target.value })}
        />
      </div>
    );
  };

  const renderGroups = (groups) => (
    <>
      <div className="set-grid">
        {groups.map((g) => (
          <section className="set-card" key={g.title}>
            <h3>{g.title}</h3>
            <p className="set-card-hint">{g.hint}</p>
            {g.keys.map(renderRow)}
            {g.title === 'Action list' && (
              <div className="set-sig-actions">
                <button className="btn btn-secondary btn-sm" onClick={sendActionListNow}
                  disabled={busy === 'digest'}>
                  {busy === 'digest' ? 'Sending…' : 'Send it now'}
                </button>
                <span className="field-hint">
                  Goes to everyone subscribed, ignoring the schedule. Save any changes first.
                </span>
              </div>
            )}
          </section>
        ))}
      </div>
      <div className="set-actions">
        <button
          className="btn btn-primary"
          onClick={() => saveSettings(groups.flatMap((g) => g.keys))}
          disabled={busy === 'save'}
        >
          {busy === 'save' ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </>
  );

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>Settings</h2>
            <p className="page-subtitle">
              Automation schedules, certificate configuration, who gets notified, and the health
              of everything the app depends on.
            </p>
          </div>
        </div>

        <Banner kind="success" message={success} onDismiss={() => setSuccess('')} />
        <Banner kind="error" message={error} onDismiss={() => setError('')} />

        <div className="tab-bar">
          {TABS.filter((t) => !t.superAdminOnly || isSuperAdmin).map((t) => (
            <button
              key={t.key}
              className={`tab${tab === t.key ? ' is-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ------------------------------------------------ Automation */}
        {tab === 'automation' && isSuperAdmin && renderGroups(GROUPS.automation)}

        {/* ------------------------------------------ Certificate module */}
        {tab === 'certificate' && isSuperAdmin && (
          <>
            <div className="set-grid">
              {GROUPS.certificate.map((g) => (
                <section className="set-card" key={g.title}>
                  <h3>{g.title}</h3>
                  <p className="set-card-hint">{g.hint}</p>
                  {g.keys.map(renderRow)}
                </section>
              ))}

              <section className="set-card">
                <h3>Signature</h3>
                <p className="set-card-hint">
                  Attached inline to every Certificate of Good Standing email, whoever presses
                  Send — a certificate is issued by the Agency, not by an individual manager.
                  Future generators get their own signature; this one is not shared.
                </p>

                {cgsModule?.hasSignature ? (
                  <div className="set-sig">
                    <img
                      src={`${API_BASE}/api/modules/${CGS}/signature?v=${sigVersion}`}
                      alt="Certificate of Good Standing signature"
                    />
                  </div>
                ) : (
                  <div className="callout callout-warning set-sig-empty">
                    <span className="callout-label">Not set</span>
                    Client emails go out unsigned until a signature is uploaded.
                  </div>
                )}

                {cgsModule?.signatureUploadedBy && (
                  <p className="set-sig-meta">
                    {cgsModule.signatureFileName} · uploaded by {cgsModule.signatureUploadedBy}
                    {cgsModule.signatureUploadedAt ? ` · ${formatDateTime(cgsModule.signatureUploadedAt)}` : ''}
                  </p>
                )}

                <div className="set-sig-actions">
                  <label className="btn btn-primary btn-sm upload-btn">
                    {busy === 'modsig' ? 'Uploading…' : cgsModule?.hasSignature ? 'Replace' : 'Upload signature'}
                    <input type="file" accept="image/png,image/jpeg,image/gif"
                      onChange={(e) => uploadModuleSignature(e.target.files[0])} disabled={busy === 'modsig'} />
                  </label>
                  {cgsModule?.hasSignature && (
                    <button className="btn btn-danger btn-sm" onClick={removeModuleSignature} disabled={busy === 'modsig'}>
                      Remove
                    </button>
                  )}
                </div>
                <span className="field-hint">PNG, JPG or GIF under 2 MB. ~400px wide works best.</span>
              </section>
            </div>

            <div className="set-actions">
              <button
                className="btn btn-primary"
                onClick={() => saveSettings(GROUPS.certificate.flatMap((g) => g.keys))}
                disabled={busy === 'save'}
              >
                {busy === 'save' ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </>
        )}

        {/* ---------------------------------------------- Notifications */}
        {tab === 'notifications' && (
          <>
            <section className="set-card set-card-full">
              <h3>My notifications</h3>
              <p className="set-card-hint">
                What you personally receive, per module. Turning something off here only affects you.
              </p>

              {!mySubs ? <Loading /> : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        {notifTypes.map((t) => <th key={t.key} className="nowrap">{t.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {mySubs.map((m) => (
                        <tr key={m.ModuleKey}>
                          <td><strong>{m.ModuleName}</strong></td>
                          {notifTypes.map((t) => (
                            <td key={t.key} className="set-check-cell">
                              <input
                                type="checkbox"
                                checked={!!m[t.key]}
                                onChange={(e) => toggleMySub(m.ModuleKey, t.key, e.target.checked)}
                                aria-label={`${t.label} for ${m.ModuleName}`}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <ul className="set-legend">
                {notifTypes.map((t) => (
                  <li key={t.key}><strong>{t.label}</strong> — {t.description}</li>
                ))}
              </ul>
            </section>

            {isSuperAdmin && (
              <section className="set-card set-card-full">
                <h3>Who gets Certificate of Good Standing notifications</h3>
                <p className="set-card-hint">
                  Only the people ticked here are emailed about this module. A future generator
                  will have its own list, so nobody is subscribed to it by default.
                </p>

                {!matrix ? <Loading /> : (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Person</th>
                          <th className="nowrap">Role</th>
                          {notifTypes.map((t) => <th key={t.key} className="nowrap">{t.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {matrix.map((u) => (
                          <tr key={u.UserId} className={u.IsActive ? '' : 'set-inactive'}>
                            <td>
                              <strong>{u.FirstName} {u.LastName}</strong>
                              <span className="set-sub">{u.Email}</span>
                            </td>
                            <td className="nowrap">
                              <span className={`badge ${u.Role === 'Super Admin' ? 'badge-indigo' : 'badge-blue'}`}>{u.Role}</span>
                              {!u.IsActive && <span className="badge badge-red set-ml">Disabled</span>}
                            </td>
                            {notifTypes.map((t) => (
                              <td key={t.key} className="set-check-cell">
                                <input
                                  type="checkbox"
                                  checked={!!u[t.key]}
                                  onChange={(e) => toggleMatrix(u.UserId, t.key, e.target.checked)}
                                  aria-label={`${t.label} for ${u.Email}`}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="set-actions">
                  <button className="btn btn-secondary" onClick={runExpirySweep} disabled={busy === 'sweep'}>
                    {busy === 'sweep' ? 'Running…' : 'Run expiry check now'}
                  </button>
                  <span className="field-hint set-inline-hint">
                    Sends the expiry warning immediately for anything inside the window, instead of
                    waiting for the next scheduled sweep.
                  </span>
                </div>
              </section>
            )}

            {isSuperAdmin && (
              <section className="set-card set-card-full">
                <h3>Other recipients</h3>
                <p className="set-card-hint">
                  Addresses that receive Certificate of Good Standing notifications without having
                  an account here — a director, an auditor, or a shared inbox. They cannot sign in.
                </p>

                <div className="set-add-recipient">
                  <input
                    type="email"
                    placeholder="name@company.co.za"
                    value={newRecipient.email}
                    onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                    aria-label="Recipient email address"
                  />
                  <input
                    type="text"
                    placeholder="Name (optional)"
                    value={newRecipient.name}
                    onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                    aria-label="Recipient name"
                  />
                  {notifTypes.map((t) => (
                    <label className="set-add-check" key={t.key} title={t.description}>
                      <input
                        type="checkbox"
                        checked={!!newRecipient[t.key]}
                        onChange={(e) => setNewRecipient({ ...newRecipient, [t.key]: e.target.checked })}
                      />
                      <span>{t.label}</span>
                    </label>
                  ))}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={addRecipient}
                    disabled={busy === 'addrecipient' || !newRecipient.email.trim()}
                  >
                    {busy === 'addrecipient' ? 'Adding…' : 'Add'}
                  </button>
                </div>

                {!recipients ? <Loading /> : recipients.length === 0 ? (
                  <p className="set-card-hint set-none">
                    Nobody outside the system is on the list yet.
                  </p>
                ) : (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Recipient</th>
                          {notifTypes.map((t) => <th key={t.key} className="nowrap">{t.label}</th>)}
                          <th className="nowrap">Added</th>
                          <th className="nowrap"> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.map((r) => (
                          <tr key={r.Id}>
                            <td>
                              <strong>{r.Name || r.Email}</strong>
                              {r.Name && <span className="set-sub">{r.Email}</span>}
                            </td>
                            {notifTypes.map((t) => (
                              <td key={t.key} className="set-check-cell">
                                <input
                                  type="checkbox"
                                  checked={!!r[t.key]}
                                  onChange={(e) => toggleRecipient(r.Id, t.key, e.target.checked)}
                                  aria-label={`${t.label} for ${r.Email}`}
                                />
                              </td>
                            ))}
                            <td className="nowrap set-sub">{r.CreatedBy || '—'}</td>
                            <td className="nowrap">
                              <button className="btn btn-ghost btn-sm set-remove" onClick={() => removeRecipient(r)}>
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {/* ------------------------------------------------ Google Sheet */}
        {tab === 'sheet' && (
          !sheet ? <Loading label="Reading the sheet…" /> : (
            <>
              {sheet.readError && (
                <div className="callout callout-red">
                  <span className="callout-label">Cannot read the sheet</span>{sheet.readError}
                </div>
              )}
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>App field</th><th>Sheet column</th><th>Most recent value</th></tr>
                  </thead>
                  <tbody>
                    {sheet.fields.map((f) => {
                      const selected = mapping[f.field] || '';
                      const sample = selected && sheet.sampleRow ? sheet.sampleRow[selected] : '';
                      return (
                        <tr key={f.field}>
                          <td>
                            <strong>{f.label}</strong>
                            {f.required && <span className="set-required">required</span>}
                          </td>
                          <td>
                            <select
                              value={selected}
                              onChange={(e) => setMapping({ ...mapping, [f.field]: e.target.value || null })}
                              disabled={!isSuperAdmin || sheet.headers.length === 0}
                              aria-label={`Sheet column for ${f.label}`}
                            >
                              <option value="">— not mapped —</option>
                              {sheet.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </td>
                          <td className="set-sample">{sample || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {isSuperAdmin && (
                <div className="set-actions">
                  <button className="btn btn-secondary" onClick={autoDetect} disabled={busy === 'detect'}>
                    {busy === 'detect' ? 'Detecting…' : 'Suggest from headers'}
                  </button>
                  <button className="btn btn-primary" onClick={saveMapping} disabled={busy === 'mapping'}>
                    {busy === 'mapping' ? 'Saving…' : 'Save mapping'}
                  </button>
                </div>
              )}
            </>
          )
        )}

        {/* -------------------------------------------------- My profile */}
        {tab === 'profile' && profile && (
          <div className="set-grid">
            <section className="set-card">
              <h3>Account</h3>
              <div className="set-row"><span className="set-row-label">Name</span>
                <span className="set-static">{profile.firstName} {profile.lastName}</span></div>
              <div className="set-row"><span className="set-row-label">Email</span>
                <span className="set-static">{profile.email}</span></div>
              <div className="set-row"><span className="set-row-label">Role</span>
                <span className="set-static">{profile.role}</span></div>
              <div className="set-row"><span className="set-row-label">Last sign-in</span>
                <span className="set-static">{formatDateTime(profile.lastLoginAt) || 'This is your first'}</span></div>
            </section>

            <section className="set-card">
              <h3>Change password</h3>
              <p className="set-card-hint">At least 8 characters.</p>
              <div className="set-row set-row-wide">
                <span className="set-row-label">Current</span>
                <input className="set-control" type="password" value={passwords.currentPassword}
                  onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })} />
              </div>
              <div className="set-row set-row-wide">
                <span className="set-row-label">New</span>
                <input className="set-control" type="password" value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })} />
              </div>
              <div className="set-row set-row-wide">
                <span className="set-row-label">Confirm</span>
                <input className="set-control" type="password" value={passwords.confirm}
                  onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} />
              </div>
              <div className="set-actions">
                <button className="btn btn-secondary btn-sm" onClick={changePassword}
                  disabled={busy === 'password' || !passwords.currentPassword || passwords.newPassword.length < 8}>
                  {busy === 'password' ? 'Changing…' : 'Change password'}
                </button>
              </div>
            </section>

            <section className="set-card">
              <h3>Check email delivery</h3>
              <p className="set-card-hint">
                Sends a test to {profile.email} through Microsoft 365. No client is contacted.
              </p>
              <div className="set-actions">
                <button className="btn btn-secondary btn-sm" onClick={sendTestEmail} disabled={busy === 'testmail'}>
                  {busy === 'testmail' ? 'Sending…' : 'Send me a test email'}
                </button>
              </div>
            </section>
          </div>
        )}

        {/* ------------------------------------------------------- Health */}
        {tab === 'health' && (
          !health ? <Loading label="Checking connections…" /> : (
            <>
              <div className="set-health">
                <HealthCard title="Database" ok={health.database?.ok}
                  detail={health.database?.ok ? `Connected via ${health.database.driver}` : health.database?.error} />
                <HealthCard title="Microsoft 365 email" ok={health.email?.ok}
                  detail={health.email?.ok ? `Sending as ${health.email.from}` : health.email?.error} />
                <HealthCard title="PDF conversion" ok={health.pdf?.ok}
                  detail={health.pdf?.ok ? health.pdf.version : health.pdf?.error} />
                <HealthCard title="Google Sheet" ok={health.sheets?.ok}
                  detail={health.sheets?.ok
                    ? (health.sheets.warning || `Reading "${health.sheets.configuredTab}"`)
                    : health.sheets?.error}
                  extra={health.sheets?.serviceAccount ? `Shared with: ${health.sheets.serviceAccount}` : null} />
              </div>

              {scheduler && (
                <section className="set-card set-card-full">
                  <h3>Background jobs</h3>
                  <div className="set-row"><span className="set-row-label">Last sheet check</span>
                    <span className="set-static">
                      {scheduler.lastPollAt ? formatDateTime(scheduler.lastPollAt) : 'Not run yet'}
                      {scheduler.lastPollError && <span className="set-error"> — {scheduler.lastPollError}</span>}
                    </span></div>
                  <div className="set-row"><span className="set-row-label">Imported last check</span>
                    <span className="set-static">{scheduler.lastPollResult?.imported ?? 0}</span></div>
                  <div className="set-row"><span className="set-row-label">Last work reminder</span>
                    <span className="set-static">
                      {scheduler.lastReminderAt ? formatDateTime(scheduler.lastReminderAt) : 'Not run yet'}
                    </span></div>
                  <div className="set-row"><span className="set-row-label">Last expiry sweep</span>
                    <span className="set-static">
                      {scheduler.lastExpirySweepAt ? formatDateTime(scheduler.lastExpirySweepAt) : 'Not run yet'}
                      {scheduler.lastExpiryCount != null && ` · ${scheduler.lastExpiryCount} warned`}
                      {scheduler.lastExpiryError && <span className="set-error"> — {scheduler.lastExpiryError}</span>}
                    </span></div>
                </section>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}

function HealthCard({ title, ok, detail, extra }) {
  return (
    <div className={`set-health-card${ok ? ' is-ok' : ' is-bad'}`}>
      <div className="set-health-head">
        <span className={`badge ${ok ? 'badge-green' : 'badge-red'}`}>{ok ? 'OK' : 'Problem'}</span>
        <strong>{title}</strong>
      </div>
      <p>{detail || '—'}</p>
      {extra && <p className="set-health-extra">{extra}</p>}
    </div>
  );
}

export default Settings;
